import { useState } from "react"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import toast from "react-hot-toast"
import { FileDown } from "lucide-react"
import type { EquipmentSpec, SpecItem, SpecPart } from "../hooks/useSpecs"
import { ensureKoreanFont, KR_FONT } from "../lib/pdfKoreanFont"

interface GroupedItems {
  category: string
  items: SpecItem[]
}

// HTML 문자열에서 태그를 제거하되, 블록/줄바꿈 태그(<p>, <br>, <div>, <li>)는
// 개행(\n)으로 변환하여 입력 시의 Enter/줄바꿈을 PDF에도 그대로 반영한다.
function stripHtml(html?: string): string {
  if (!html) return ""
  const s = html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
  const tmp = document.createElement("div")
  tmp.innerHTML = s
  const text = tmp.textContent || tmp.innerText || ""
  return text.replace(/\n{3,}/g, "\n\n").replace(/[ \t]+\n/g, "\n").trim()
}

function partsToText(parts?: SpecPart[]): string {
  if (!parts || parts.length === 0) return ""
  return parts
    .map((p) => {
      const name = p.partName || p.partNumber || "-"
      const num = p.partNumber && p.partName ? ` (${p.partNumber})` : ""
      const qty = ` ×${p.quantity ?? 1}`
      const maker = p.maker ? ` / ${p.maker}` : ""
      return `${name}${num}${qty}${maker}`
    })
    .join("\n")
}

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" }) : "-"

// 대분류 컬러바 색상 (Web 화면 CATEGORY_COLORS 와 동일 계열, RGB)
const PDF_BAR_COLORS: [number, number, number][] = [
  [249, 115, 22], // orange-500
  [14, 165, 233], // sky-500
  [16, 185, 129], // emerald-500
  [139, 92, 246], // violet-500
  [244, 63, 94], // rose-500
  [245, 158, 11], // amber-500
  [20, 184, 166], // teal-500
  [99, 102, 241], // indigo-500
]
const pdfBarColor = (idx: number) => PDF_BAR_COLORS[idx % PDF_BAR_COLORS.length]

const PAGE_WIDTH = 210 // A4 mm
const MARGIN = 14 // 좌우/상하 여백(mm)
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2

/**
 * 제작 사양서 PDF 출력 (방안 C: 벡터 텍스트 + jspdf-autotable).
 *
 * - 텍스트/표를 이미지가 아닌 실제 벡터로 그려 화질 저하가 없고, 텍스트 선택/검색이 가능.
 * - 용량이 이미지 방식(수 MB~20MB) 대비 크게 감소.
 * - autoTable 이 row 단위 페이지 분할을 자동 처리하여 표 잘림이 발생하지 않음.
 * - 한글 표시를 위해 Noto Sans KR 폰트를 런타임에 등록.
 */
export function SpecPdfExport({
  spec,
  grouped,
}: {
  spec: EquipmentSpec
  grouped: GroupedItems[]
}) {
  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    if (grouped.length === 0) {
      toast.error("출력할 사양 항목이 없습니다.")
      return
    }
    setExporting(true)
    const loadingId = toast.loading("PDF를 생성하는 중...")
    try {
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true })
      await ensureKoreanFont(pdf)

      // ===== 1부: 요구사항 사양서 =====
      let y = drawDocHeader(pdf, spec, true)
      y = drawSectionTitle(pdf, "1. 요구 사양서", y)
      grouped.forEach((group, gIdx) => {
        y = drawCategoryTable(
          pdf,
          {
            category: group.category,
            barColor: pdfBarColor(gIdx),
            head: ["중분류", "요구 사양", "비고"],
            colWidths: [32, null, 40],
            body: group.items.map((item) => [
              item.subCategory || "-",
              stripHtml(item.requirement) || "-",
              stripHtml(item.remark) || "-",
            ]),
          },
          y
        )
      })

      // ===== 2부: 제작 가이드라인 + 사용 부품 (새 페이지) =====
      pdf.addPage()
      y = drawDocHeader(pdf, spec, false)
      y = drawSectionTitle(pdf, "2. 제작 가이드라인 및 사용 부품", y)
      grouped.forEach((group, gIdx) => {
        y = drawCategoryTable(
          pdf,
          {
            category: group.category,
            barColor: pdfBarColor(gIdx),
            head: ["중분류", "제작 가이드라인", "사용 부품"],
            colWidths: [32, null, 46],
            body: group.items.map((item) => [
              item.subCategory || "-",
              stripHtml(item.guideline) || "-",
              partsToText(item.parts) || "-",
            ]),
          },
          y
        )
      })

      drawFooter(pdf, spec)

      const safeTitle = (spec.specTitle || "제작사양서").replace(/[\\/:*?"<>|]/g, "_")
      pdf.save(`${safeTitle}_v${spec.currentVersion}.pdf`)
      toast.success("PDF가 저장되었습니다.", { id: loadingId })
    } catch (e) {
      console.error("사양서 PDF 생성 실패:", e)
      toast.error("PDF 생성에 실패했습니다. (한글 폰트 로드 실패 가능)", { id: loadingId })
    } finally {
      setExporting(false)
    }
  }

  return (
    <button
      onClick={handleExport}
      disabled={exporting}
      className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-60"
      title="사양서 PDF 출력"
    >
      <FileDown className="w-4 h-4" />
      {exporting ? "생성 중..." : "PDF 출력"}
    </button>
  )
}

// ===== 벡터 그리기 헬퍼 =====

// 문서 상단 헤더(브랜드/제목/메타)를 벡터로 그리고, 다음 y 좌표(mm)를 반환
function drawDocHeader(pdf: jsPDF, spec: EquipmentSpec, full: boolean): number {
  let y = MARGIN

  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(9)
  pdf.setTextColor(245, 158, 11)
  pdf.text("PAVETECH 제작 사양서", MARGIN, y + 2)

  pdf.setFontSize(full ? 16 : 13)
  pdf.setTextColor(17, 24, 39)
  const titleLines = pdf.splitTextToSize(spec.specTitle || "-", CONTENT_WIDTH)
  pdf.text(titleLines, MARGIN, y + 9)
  y += 9 + titleLines.length * (full ? 6 : 5)

  if (full) {
    pdf.setFont(KR_FONT, "normal")
    pdf.setFontSize(9)
    pdf.setTextColor(55, 65, 81)
    const meta1 = `원청사: ${spec.sourceCompany || "-"}    문서번호: ${spec.docNumber || "-"}`
    const meta2 = `현재 버전: ${spec.lastChangesetName ? spec.lastChangesetName : "미개정"}    마지막 수정일: ${spec.lastApprovedAt ? fmtDate(spec.lastApprovedAt) : "승인 이력 없음"}`
    const meta3 = `출력일: ${fmtDate(new Date().toISOString())}`
    pdf.text(meta1, MARGIN, y + 4)
    pdf.text(meta2, MARGIN, y + 9)
    pdf.text(meta3, MARGIN, y + 14)
    y += 18
  } else {
    y += 4
  }

  // 하단 구분선
  pdf.setDrawColor(245, 158, 11)
  pdf.setLineWidth(0.8)
  pdf.line(MARGIN, y, PAGE_WIDTH - MARGIN, y)
  return y + 6
}

// 섹션 제목을 벡터로 그리고 다음 y 반환
function drawSectionTitle(pdf: jsPDF, title: string, y: number): number {
  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(12)
  pdf.setTextColor(146, 64, 14)
  pdf.text(title, MARGIN, y + 4)
  return y + 10
}

interface CategoryTableConfig {
  category: string
  barColor: [number, number, number]
  head: string[]
  colWidths: (number | null)[] // null 이면 남는 폭 자동 배분
  body: string[][]
}

// 대분류 헤더(컬러바 + 대분류명) + autoTable 을 그리고 다음 y 반환.
// autoTable 이 페이지 분할을 자동 처리하므로 row 잘림이 없다.
function drawCategoryTable(pdf: jsPDF, cfg: CategoryTableConfig, y: number): number {
  const pageHeight = pdf.internal.pageSize.getHeight()
  // 표와 표 사이 간격
  y += 4

  // 대분류 헤더가 페이지 하단에 걸치면 새 페이지로
  if (y + 12 > pageHeight - MARGIN) {
    pdf.addPage()
    y = MARGIN
  }

  // 대분류 헤더 바 (컬러바 + 배경 + 텍스트)
  const [r, g, b] = cfg.barColor
  const headerH = 8
  pdf.setFillColor(249, 250, 251)
  pdf.rect(MARGIN, y, CONTENT_WIDTH, headerH, "F")
  pdf.setFillColor(r, g, b)
  pdf.rect(MARGIN, y, 1.8, headerH, "F")
  pdf.setDrawColor(229, 231, 235)
  pdf.setLineWidth(0.2)
  pdf.rect(MARGIN, y, CONTENT_WIDTH, headerH, "S")
  pdf.setFont(KR_FONT, "bold")
  pdf.setFontSize(10)
  pdf.setTextColor(17, 24, 39)
  pdf.text(cfg.category || "-", MARGIN + 4, y + 5.5)
  y += headerH

  // 컬럼 폭 계산: null 컬럼에 남는 폭 배분
  const fixed = cfg.colWidths.reduce((s: number, w) => s + (w || 0), 0)
  const autoCount = cfg.colWidths.filter((w) => w == null).length
  const autoW = autoCount > 0 ? (CONTENT_WIDTH - fixed) / autoCount : 0
  const columnStyles: Record<number, { cellWidth: number }> = {}
  cfg.colWidths.forEach((w, i) => {
    columnStyles[i] = { cellWidth: w == null ? autoW : w }
  })

  autoTable(pdf, {
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [cfg.head],
    body: cfg.body,
    theme: "grid",
    styles: {
      font: KR_FONT,
      fontStyle: "normal",
      fontSize: 9,
      cellPadding: 2,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
      textColor: [17, 24, 39],
      valign: "top",
      overflow: "linebreak",
    },
    headStyles: {
      font: KR_FONT,
      fontStyle: "bold",
      fillColor: [243, 244, 246],
      textColor: [55, 65, 81],
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    columnStyles,
    // 첫 컬럼(중분류)만 볼드 처리
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 0) {
        data.cell.styles.fontStyle = "bold"
      }
      if (data.section === "body" && data.column.index === 2) {
        data.cell.styles.textColor = [55, 65, 81]
      }
    },
  })

  // autoTable 실행 후 최종 y
  // @ts-expect-error - lastAutoTable 은 플러그인이 주입하는 런타임 속성
  const finalY: number = pdf.lastAutoTable?.finalY ?? y
  return finalY
}

// 마지막 페이지들 하단에 푸터 텍스트를 그린다(전체 페이지 반복).
function drawFooter(pdf: jsPDF, spec: EquipmentSpec) {
  const pageCount = pdf.getNumberOfPages()
  const pageHeight = pdf.internal.pageSize.getHeight()
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p)
    pdf.setFont(KR_FONT, "normal")
    pdf.setFontSize(8)
    pdf.setTextColor(156, 163, 175)
    const text = `페이브텍 관리 시스템 · 승인된 최신 버전(v${spec.currentVersion}) 기준 · ${p}/${pageCount}`
    pdf.text(text, PAGE_WIDTH / 2, pageHeight - 8, { align: "center" })
  }
}
